import { formatDate, formatMoney } from '@/lib/format';

/*
  "Needs you today" (02-dashboard idea 3) — one ranked list instead of separate empty cards.

  buildNeedsItems(data) turns GET /dashboard into rows: stages awaiting approval, blocked shop drawings
  grouped by project, late supplier deliveries (one row for all of them), projects past their end date,
  submitted invoices (one row) and variation orders still Proposed, plus open tasks assigned to me.
  A section the role cannot open is null in the response and gives no rows and no tab.

  Row: { key, category, tone, status, title, code?, sub: [part], age (days | null), ageTone, action: { label, to },
         context } where a sub part is a string, { code } (mono, e.g. LPO-2025-001) or { money } (bold amount),
  and `context` names the row for its action button ("Open stage: Luxor Tower · BOQ & Design"), so a list
  of 16 "Open stage" links still reads apart for a screen reader.
  Ranking: red first (late, overdue, blocked), then amber (awaiting approval), then blue / violet / gray;
  inside a tone the oldest first.
  Sub line order: the fact that matters comes first, because a long line is cut at its end on a desktop row — the
  money of late deliveries, the stage a late project is stuck at, the person a blocked group waits on.
*/

// `empty` = what an empty tab says (all projects / only mine), never a blank card.
export const NEEDS_CATEGORIES = [
  {
    key: 'approvals', label: 'Approvals', one: 'stage approval', many: 'stage approvals',
    empty: { title: 'No stage approvals waiting', everyone: 'Stages marked Submitted on active projects show up here.', mine: 'No stage of your active projects is marked Submitted.' },
  },
  {
    key: 'drawings', label: 'Drawings', one: 'project with blocked drawings', many: 'projects with blocked drawings',
    empty: { title: 'No blocked shop drawings', everyone: 'Drawings marked Blocked show up here, grouped by project.', mine: 'No drawing of your projects is marked Blocked.' },
  },
  {
    key: 'supply', label: 'Supply', one: 'supplier delivery', many: 'supplier deliveries',
    empty: { title: 'No late deliveries', everyone: 'LPOs past their expected date, and deliveries due this week, show up here.', mine: 'No LPO of your projects is late or due this week.' },
  },
  {
    key: 'finance', label: 'Finance', one: 'finance item', many: 'finance items',
    empty: { title: 'Nothing waiting on payment', everyone: 'Submitted invoices and variation orders still Proposed show up here.', mine: 'Your projects have no submitted invoices or proposed variation orders.' },
  },
  {
    key: 'projects', label: 'Projects', one: 'late project', many: 'late projects',
    empty: { title: 'No project is past its end date', everyone: 'Active projects that ran past their end date show up here.', mine: 'None of your active projects ran past its end date.' },
  },
  {
    key: 'tasks', label: 'Tasks', one: 'task row', many: 'task rows',
    empty: { title: 'No open tasks assigned to you', everyone: 'Tasks assigned to you show up here.', mine: 'Tasks assigned to you show up here.' },
  },
];

/** { title, text } for a tab with no rows. */
export function emptyCategoryText(key, { mine = false } = {}) {
  const category = NEEDS_CATEGORIES.find((entry) => entry.key === key);
  if (!category) return { title: 'Nothing here', text: '' };
  return { title: category.empty.title, text: mine ? category.empty.mine : category.empty.everyone };
}

// How many rows of each kind the mixed "All" view shows before "View all" — so 16 approvals never
// push the late deliveries and blocked drawings off the card.
const ALL_VIEW_QUOTA = { supply: 1, approvals: 3, drawings: 2, projects: 1, finance: 2, tasks: 1 };
const TONE_RANK = { red: 0, amber: 1, blue: 2, violet: 3, gray: 4 };
const DUE_SOON_DAYS = 7;
const LONG_WAIT_DAYS = 365;

const plural = (count, one, many = `${one}s`) => (count === 1 ? one : many);
const clean = (value) => String(value ?? '').trim();
const hasNumber = (value) => value !== null && value !== undefined && Number.isFinite(Number(value));

/** 'Floor Plan' → 'floor plans', 'SCHEMATIC' → 'schematics'. */
export function pluralType(type) {
  const text = clean(type).toLowerCase().replace(/_/g, ' ');
  if (!text) return '';
  return /s$/.test(text) ? text : `${text}s`;
}

/** LPO-2025-001, LPO-2025-002, LPO-2025-003 → 'LPO-2025-001 · 002 · 003' (shared prefix written once). */
export function compressCodes(codes) {
  const list = (codes || []).map(clean).filter(Boolean);
  if (list.length < 2) return list.join('');
  const cut = list[0].lastIndexOf('-');
  const prefix = cut > 0 ? list[0].slice(0, cut + 1) : '';
  if (!prefix || !list.every((code) => code.startsWith(prefix))) return list.join(' · ');
  return [list[0], ...list.slice(1).map((code) => code.slice(prefix.length))].join(' · ');
}

const signedMoney = (value) => {
  const amount = Number(value) || 0;
  const text = formatMoney(Math.abs(amount), { decimals: 0 });
  return amount < 0 ? `−${text}` : amount > 0 ? `+${text}` : text;
};

function supplyRows(data) {
  const late = data.late_deliveries;
  const rows = [];
  const items = late?.items || [];
  if (items.length === 1) {
    const item = items[0];
    rows.push({
      key: `lpo-${item.id || item.lpo_number}`,
      category: 'supply',
      tone: 'red',
      status: 'Late',
      code: item.lpo_number,
      title: `· ${item.supplier ? `${item.supplier} delivery` : 'Delivery'} late`,
      sub: [
        item.total_value ? { money: formatMoney(item.total_value, { currency: item.currency || 'AED', decimals: 0 }) } : null,
        item.project_name || (item.project_missing ? 'Project deleted' : 'No project'),
        `expected ${formatDate(item.expected_date)}`,
      ],
      age: item.days_late,
      ageTone: 'red',
      action: { label: 'Chase', to: `/procurement?lpo=${encodeURIComponent(item.lpo_number || '')}` },
      context: [item.lpo_number, item.supplier].map(clean).filter(Boolean).join(' · ') || 'late delivery',
    });
  } else if (items.length > 1) {
    const suppliers = [...new Set(items.map((item) => clean(item.supplier)).filter(Boolean))];
    rows.push({
      key: 'lpo-late',
      category: 'supply',
      tone: 'red',
      status: 'Late',
      title: `${items.length} supplier deliveries late`,
      sub: [
        late.total_value ? { money: formatMoney(late.total_value, { currency: late.currency || 'AED', decimals: 0 }) } : null,
        { code: compressCodes(items.map((item) => item.lpo_number)) },
        suppliers.join(', ') || null,
      ],
      age: late.max_days_late ?? Math.max(...items.map((item) => Number(item.days_late) || 0)),
      ageTone: 'red',
      action: { label: 'Chase', to: '/procurement?filter=late' },
      context: `${items.length} late supplier deliveries`,
    });
  }
  (data.upcoming_deliveries || [])
    .filter((item) => hasNumber(item.days_until) && Number(item.days_until) <= DUE_SOON_DAYS)
    .forEach((item) => {
      rows.push({
        key: `lpo-due-${item.lpo_number}-${item.expected_date}`,
        category: 'supply',
        tone: 'blue',
        status: 'Due',
        code: item.lpo_number,
        title: `· ${item.supplier || 'Delivery'} due ${Number(item.days_until) === 0 ? 'today' : `in ${item.days_until} ${plural(Number(item.days_until), 'day')}`}`,
        sub: [item.project_name || 'No project', `expected ${formatDate(item.expected_date)}`],
        age: null,
        action: { label: 'Open LPO', to: `/procurement?lpo=${encodeURIComponent(item.lpo_number || '')}` },
        context: [item.lpo_number, item.supplier].map(clean).filter(Boolean).join(' · ') || 'delivery due',
      });
    });
  return rows;
}

function approvalRows(data) {
  return (data.pending_approvals || []).map((item) => ({
    key: `stage-${item.project_id}-${item.stage_id || item.stage_name}`,
    category: 'approvals',
    tone: 'amber',
    status: 'Awaiting',
    title: `${item.stage_name} awaiting approval`,
    sub: [
      item.project_name,
      item.project_engineer || 'No Project Engineer',
      item.last_update ? `last update ${formatDate(item.last_update)}` : 'no dated activity',
    ],
    age: hasNumber(item.age_days) ? Number(item.age_days) : null,
    ageTone: Number(item.age_days) >= LONG_WAIT_DAYS ? 'amber' : null,
    action: {
      label: 'Open stage',
      to: `/projects/${item.project_id}?tab=stages${item.stage_id ? `&stage=${encodeURIComponent(item.stage_id)}` : ''}`,
    },
    context: [item.project_name, item.stage_name].map(clean).filter(Boolean).join(' · '),
  }));
}

function drawingRows(data) {
  return (data.blocked_drawings?.by_project || []).map((item) => {
    const status = clean(item.project_status);
    const type = item.discipline && item.drawing_type
      ? `${item.discipline} ${pluralType(item.drawing_type)}`
      : item.discipline || (item.drawing_type ? pluralType(item.drawing_type) : '');
    const blocker = clean(item.blocker).replace(/_/g, ' ').toLowerCase();
    return {
      key: `drawings-${item.project_id || item.project_name}`,
      category: 'drawings',
      tone: 'red',
      status: 'Blocked',
      title: `${item.count} shop ${plural(item.count, 'drawing')} blocked`,
      sub: [
        `${item.project_name}${status && status.toLowerCase() !== 'active' ? ` (${status})` : ''}`,
        item.resp ? `resp. ${item.resp}` : null,
        blocker ? `waiting on ${blocker}` : null,
        type || null,
      ],
      age: hasNumber(item.age_days) ? Number(item.age_days) : null,
      action: {
        label: 'Review',
        to: `/shop-drawings?tab=blocked&project=${encodeURIComponent(item.project_name || '')}`,
      },
      context: `${clean(item.project_name) || 'No project'} · ${item.count} blocked ${plural(item.count, 'drawing')}`,
    };
  });
}

function lateProjectRows(data) {
  return (data.late_projects || []).map((item) => ({
    key: `late-project-${item.project_id}`,
    category: 'projects',
    tone: 'red',
    status: 'Late',
    title: `${item.project_name} is ${item.days_late} ${plural(Number(item.days_late), 'day')} past its end date`,
    sub: [
      item.stage_number ? `stage ${item.stage_number} of ${item.stage_count}, ${item.current_stage}` : null,
      item.project_engineer || 'No Project Engineer',
      `end ${formatDate(item.end_date)}`,
    ],
    age: Number(item.days_late),
    ageTone: 'red',
    action: { label: 'Update dates', to: `/projects/${item.project_id}` },
    context: clean(item.project_name),
  }));
}

// A VO's amount shows only to roles with Invoicing (owner rule 13 Oct 2026): the server leaves `financial_impact`
// out for everyone else and marks the VO `finance_hidden` — then the row has no amount (never "AED 0").
const voMoney = (vo, showFinance) => (
  showFinance && !vo.finance_hidden && vo.financial_impact !== null && vo.financial_impact !== undefined && vo.financial_impact !== ''
    ? { money: signedMoney(vo.financial_impact) }
    : null
);

function financeRows(data, { showFinance = true } = {}) {
  const rows = [];
  // Submitted invoices are invoice data: only for roles with Invoicing (the server sends null to the others).
  const unpaid = showFinance ? data.unpaid_invoices : null;
  if (unpaid?.count) {
    const oldest = unpaid.oldest;
    rows.push({
      key: 'invoices-submitted',
      category: 'finance',
      tone: 'blue',
      status: 'Submitted',
      title: `${unpaid.count} ${plural(unpaid.count, 'invoice')} submitted, not yet paid`,
      sub: oldest
        ? [
          `Oldest: ${oldest.project_name || 'no project'}`,
          { money: formatMoney(oldest.amount, { decimals: 0 }) },
          formatDate(oldest.date),
          oldest.invoice_type || null,
        ]
        : [{ money: formatMoney(unpaid.total_amount, { decimals: 0 }) }],
      age: hasNumber(oldest?.age_days) ? Number(oldest.age_days) : null,
      action: { label: 'Follow up', to: '/invoicing?status=Submitted' },
      context: `${unpaid.count} submitted ${plural(unpaid.count, 'invoice')}`,
    });
  }
  (data.proposed_variations || [])
    // A VO whose project was deleted cannot be opened or decided any more — it is not shown here.
    .filter((vo) => vo.project_id && !vo.project_missing)
    .forEach((vo) => {
      rows.push({
        key: `vo-${vo.id || vo.vo_number}`,
        category: 'finance',
        tone: 'violet',
        status: 'Variation',
        code: vo.vo_number,
        title: `proposed${vo.description ? ` · ${vo.description}` : ''}`,
        sub: [vo.project_name || null, voMoney(vo, showFinance), 'waiting for client decision'],
        age: hasNumber(vo.age_days) ? Number(vo.age_days) : null,
        action: { label: 'Open VO', to: `/projects/${vo.project_id}?tab=variations` },
        context: [vo.vo_number, vo.project_name].map(clean).filter(Boolean).join(' · '),
      });
    });
  return rows;
}

function taskRows(data) {
  const mine = data.my_tasks;
  if (!mine?.open) return [];
  const items = mine.items || [];
  const overdue = Number(mine.overdue) || 0;
  const oldestOverdue = Math.max(0, ...items.map((item) => Number(item.days_overdue) || 0));
  return [{
    key: 'my-tasks',
    category: 'tasks',
    tone: overdue ? 'red' : 'gray',
    status: overdue ? 'Overdue' : 'Open',
    title: `${mine.open} open ${plural(mine.open, 'task')} assigned to you`,
    sub: [
      overdue ? `${overdue} overdue` : null,
      items.slice(0, 2).map((item) => item.name).join(', ') + (mine.open > 2 ? ` +${mine.open - 2} more` : ''),
    ],
    age: overdue ? oldestOverdue : null,
    ageTone: overdue ? 'red' : null,
    action: {
      label: 'Open tasks',
      to: mine.open === 1 && items[0]?.id ? `/tasks?task=${encodeURIComponent(items[0].id)}` : '/tasks',
    },
    context: `${mine.open} assigned to you`,
  }];
}

export function compareNeeds(a, b) {
  const tone = (TONE_RANK[a.tone] ?? 9) - (TONE_RANK[b.tone] ?? 9);
  if (tone) return tone;
  const ageA = hasNumber(a.age) ? Number(a.age) : -1;
  const ageB = hasNumber(b.age) ? Number(b.age) : -1;
  if (ageA !== ageB) return ageB - ageA;
  return String(a.title).localeCompare(String(b.title));
}

/**
 * Every row, ranked. `showFinance` = the user has Invoicing (canAccessInvoicing): without it the submitted-invoices
 * row is left out and VO rows carry no amount.
 */
export function buildNeedsItems(data, { showFinance = true } = {}) {
  if (!data) return [];
  return [
    ...supplyRows(data),
    ...approvalRows(data),
    ...drawingRows(data),
    ...lateProjectRows(data),
    ...financeRows(data, { showFinance }),
    ...taskRows(data),
  ].sort(compareNeeds);
}

/** Which tabs this user gets: a category whose source sections are all null (no permission) has none. */
export function availableCategories(data) {
  if (!data) return [];
  const has = (...keys) => keys.some((key) => data[key] !== null && data[key] !== undefined);
  const available = {
    approvals: has('pending_approvals'),
    drawings: has('blocked_drawings'),
    supply: has('late_deliveries', 'upcoming_deliveries'),
    finance: has('unpaid_invoices', 'proposed_variations'),
    projects: has('late_projects'),
    tasks: Boolean(data.my_tasks?.open),
  };
  return NEEDS_CATEGORIES.filter((category) => available[category.key]);
}

export function countByCategory(items) {
  const counts = {};
  items.forEach((item) => { counts[item.category] = (counts[item.category] || 0) + 1; });
  return counts;
}

/** The mixed "All" view: up to `limit` rows, a few of each kind (ALL_VIEW_QUOTA), topped up by rank. */
export function pickAllView(items, limit = 9) {
  const picked = new Set();
  const used = {};
  items.forEach((item) => {
    if (picked.size >= limit) return;
    const quota = ALL_VIEW_QUOTA[item.category] ?? 1;
    if ((used[item.category] || 0) < quota) {
      picked.add(item);
      used[item.category] = (used[item.category] || 0) + 1;
    }
  });
  items.forEach((item) => {
    if (picked.size < limit) picked.add(item);
  });
  return items.filter((item) => picked.has(item));
}

/** "+13 more stage approvals · +8 projects with blocked drawings" for what the short view leaves out. */
export function hiddenSummary(items, shown, max = 2) {
  const shownSet = new Set(shown);
  const hidden = countByCategory(items.filter((item) => !shownSet.has(item)));
  return NEEDS_CATEGORIES
    .filter((category) => hidden[category.key])
    .sort((a, b) => hidden[b.key] - hidden[a.key])
    .slice(0, max)
    .map((category) => `+${hidden[category.key]} more ${plural(hidden[category.key], category.one, category.many)}`)
    .join(' · ');
}
